import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useEffect, useState } from "react";
import { FormLabel, Grid, Typography, makeStyles } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import clsx from "clsx";
import { defineMessages, useIntl } from "react-intl";
import { DployFormControl, DployTextField, identityRecordOfFieldEditorOptions } from "@ploy-ui/form-fields";
import { EncumberanceObjectDisplay, PendingButton, EncumberanceSortByMessageSeverity, EncumberanceMessages, EncumberanceNormalizeRegNo, EncumberanceSearchErrorText, EncumberanceCheckContextProvider, useEncumberanceCheckContext } from "@ploy-ui/core";
import { useDispatch, useVariableData } from "@ploy-lib/calculation";
import { useAppLoad } from "../../appLoadContext";
const messages = defineMessages({
    objectMissing: {
        id: "template-form.reg-no-search-field.object-missing",
        defaultMessage: "Søknaden har ikke et objekt som kan oppdateres."
    },
    search: {
        id: "template-form.reg-no-search-field.search",
        defaultMessage: "Søk på registreringsnummer."
    }
});
const useStyles = makeStyles(theme => ({
    root: {},
    button: {
        whiteSpace: "nowrap",
        minWidth: 0
    },
    searchResult: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    message: {
        marginBottom: theme.spacing(1)
    },
    messageError: {
        color: theme.palette.error.main
    },
    messageWarning: {
        color: theme.palette.warning.main
    },
    messageInfo: {
        color: theme.palette.info.main
    },
    iconInLabel: {
        verticalAlign: "bottom"
    }
}));
export const RegNoSearchFieldInternal = ({ id, label, placeholder, disabled: templateDisabled, margin }) => {
    const classes = useStyles();
    const intl = useIntl();
    const { refetchApp, refetchCalcRules } = useAppLoad();
    const { currentObject, searchResult, isVisible, isRegNoValid, isBusy, isSaving, fields, setField, doRegNoSearch, doSave, warnings, regNoRules: { regNoChanged, regNoCannotBeEmpty, regNoSearchMissing, docsWillResetOnChange, docsWillResetOnSave } } = useEncumberanceCheckContext();
    // ---- Local state --------------------------------------------------------
    const [feedbackMessages, setFeedbackMessages] = useState([]);
    // ---- Derived state ------------------------------------------------------
    const regNoValidationFailed = fields.regNo.length > 0 && !isRegNoValid(fields.regNo);
    const disabled = templateDisabled || currentObject?.application?.isLocked || isBusy || docsWillResetOnChange;
    const searchDisabled = disabled || !isVisible("regNoSearch") || fields.regNo.length === 0;
    // ---- Calculator sync ----------------------------------------------------
    // This component does its own search, so the search service in DR never sets the variable.
    // Keep it in sync with our own rule, or the validator blocks validateForm and submit.
    const dispatch = useDispatch();
    const { value: searchCompleteCalcRules } = useVariableData("Object", "RegNumberSearchComplete");
    const searchCompleteLocal = regNoSearchMissing ? "" : "true";
    useEffect(() => {
        if ((searchCompleteCalcRules ?? "") === searchCompleteLocal)
            return;
        dispatch({
            type: "patch",
            payload: {
                patches: [{ namespace: "Object", target: "RegNumberSearchComplete", value: searchCompleteLocal, overwrite: true }]
            }
        });
        dispatch({ type: "calculate" });
    }, [dispatch, searchCompleteCalcRules, searchCompleteLocal]);
    // ---- Messages -----------------------------------------------------------
    const showFeedbackMessage = (message) => {
        setFeedbackMessages(prev => [...prev.filter(m => m.id !== message.id), message]);
    };
    const dismissFeedbackMessage = (id) => {
        setFeedbackMessages(prev => prev.filter(m => m.id !== id));
    };
    // ---- Callbacks ----------------------------------------------------------
    const handleSearch = async () => {
        if (searchDisabled)
            return;
        await doRegNoSearch(async (result) => {
            dismissFeedbackMessage("search");
        }, async (message) => {
            showFeedbackMessage({
                id: "search",
                text: EncumberanceSearchErrorText(intl, message, fields.regNo),
                type: "error"
            });
        });
    };
    const handleSave = async () => {
        await doSave(async () => true, async (result) => true, async (result) => {
            refetchApp();
            refetchCalcRules?.();
            dismissFeedbackMessage("save");
        }, async (message) => {
            showFeedbackMessage({
                id: "save",
                text: intl.formatMessage(EncumberanceMessages.saveFailed),
                type: "error"
            });
        });
    };
    return (_jsxs(Grid, { item: true, xs: 12, container: true, className: classes.root, children: [_jsxs(Grid, { item: true, xs: 12, container: true, alignItems: "flex-start", wrap: "nowrap", children: [_jsx(Grid, { item: true, xs: true, children: _jsx(DployTextField, { id: id, label: label ?? intl.formatMessage(EncumberanceMessages.regNoTitle), placeholder: placeholder, value: fields.regNo, onKeyDown: (e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSearch();
                                }
                            }, onChange: (e) => {
                                setField("regNo")(EncumberanceNormalizeRegNo(e.target.value));
                            }, disabled: disabled, margin: margin, error: regNoValidationFailed, helperText: regNoValidationFailed ? intl.formatMessage(EncumberanceMessages.regNoInvalid) : undefined, fullWidth: true, inputProps: { autoCapitalize: "characters", spellCheck: false } }) }), isVisible("regNoSearch") && (_jsx(Grid, { item: true, children: _jsxs(DployFormControl, { margin: margin, children: [_jsx(FormLabel, { children: " " }), _jsx(PendingButton, { variant: "outlined", color: "primary", className: classes.button, disabled: searchDisabled, onClick: handleSearch, size: "large", fullWidth: true, "aria-label": intl.formatMessage(messages.search), hideChildrenWhilePending: true, children: _jsx(SearchIcon, { className: classes.iconInLabel }) })] }) })), _jsx(Grid, { item: true, children: _jsxs(DployFormControl, { margin: margin, children: [_jsx(FormLabel, { children: " " }), _jsx(PendingButton, { variant: "contained", color: "primary", className: classes.button, onClick: handleSave, disabled: disabled || !regNoChanged || regNoSearchMissing || regNoValidationFailed || regNoCannotBeEmpty || searchResult?.mustReplace || docsWillResetOnSave, pending: isSaving, size: "large", fullWidth: true, "aria-label": intl.formatMessage(EncumberanceMessages.save), hideChildrenWhilePending: true, children: _jsx(ArrowForwardIcon, { className: classes.iconInLabel }) })] }) })] }), searchResult && (_jsx(Grid, { item: true, xs: true, className: classes.searchResult, children: _jsx(EncumberanceObjectDisplay, { title: intl.formatMessage(EncumberanceMessages.objectFromSearch), object: searchResult.object, mismatch: searchResult.mismatch, isVisible: isVisible }) })), _jsx(Grid, { item: true, container: true, children: [...warnings, ...feedbackMessages]
                    .sort(EncumberanceSortByMessageSeverity)
                    .map(message => (_jsx(Grid, { item: true, children: _jsx(Typography, { className: clsx(classes.message, {
                            [classes.messageError]: message.type === "error",
                            [classes.messageWarning]: message.type === "warning",
                            [classes.messageInfo]: message.type === "info"
                        }), children: message.text }) }, message.id))) })] }));
};
export const RegNoSearchField = memo((props) => {
    const { label, placeholder, disabled, formTemplateFieldId, margin, } = props;
    const { applicationId } = useAppLoad();
    const intl = useIntl();
    if (!applicationId) {
        return (_jsx(Typography, { color: "textSecondary", children: intl.formatMessage(messages.objectMissing) }));
    }
    return (_jsx(EncumberanceCheckContextProvider, { applicationId: applicationId, canResetDocs: false, children: _jsx(RegNoSearchFieldInternal, { id: formTemplateFieldId, label: label, placeholder: placeholder, disabled: disabled, margin: margin }) }, applicationId));
});
RegNoSearchField.displayName = "RegNoSearchField";
export const EditorRegNoSearchField = identityRecordOfFieldEditorOptions({
    RegNoSearchField: {
        editableOptions: {
            placeholder: true
        }
    }
});
//# sourceMappingURL=RegNoSearchField.js.map